import React, { Component } from 'react';
import Dialog from 'components/theme/dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import { compose, graphql } from 'react-apollo';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import FeatureForm from 'components/features/feature-form';
import gqlMutUpdateFeature from 'graphql/mutations/update-feature-with-flag';
import gqlMutCreateFeature from 'graphql/mutations/create-feature';
import gqlMutDeleteFeature from 'graphql/mutations/delete-feature';
import gqlMutUpdateFlag from 'graphql/mutations/update-flag';
import gqlMutUpdateCondition from 'graphql/mutations/update-condition';
import gqlQuerylistUserSegments from 'graphql/queries/get-user-segments';
import gqlQuerylistConditions, {
  gqlQrylistCondsWithMultiConditionType
} from 'graphql/queries/get-flag-conditions';
import Loader from 'components/theme/loader';
import SnackBar from 'components/theme/snackbar';
import { flags, DELAY_AFTER_S3_FILE_UPLOAD, actions } from 'app-constants';

class FeatureModifier extends Component {
  state = { loading: false, error: null, completed: false, refetchTimeout: 0 };

  componentWillUnmount() {
    const { refetchTimeout } = this.state;
    clearTimeout(refetchTimeout);
  }

  onSubmit = (values) => {
    const { appId, env } = this.props;
    this.setState({ loading: true, error: null, completed: false });

    let graphqlActions;

    if (this.props.action === actions.FEATURE_ADD) {
      graphqlActions = [
        this.props.mutateCreateFeature({
          variables: {
            key: values.key,
            appId,
            desc: values.description
          }
        })
      ];
    } else if (this.props.action === actions.FEATURE_TOGGLE) {
      graphqlActions = [
        this.props.mutateUpdateFlag({
          variables: {
            key: values.key,
            appId,
            environment: env,
            enable: values.enable
          }
        })
      ];
    } else if (this.props.action === actions.FEATURE_DELETE) {
      graphqlActions = [
        this.props.mutateDeleteFeature({
          variables: {
            key: values.key,
            appId
          }
        })
      ];
    } else if (this.props.action === actions.FEATURE_EDIT) {
      graphqlActions = [
        this.props.mutateUpdateFeature({
          variables: {
            key: values.key,
            appId,
            desc: values.description,
            environment: env,
            enable: values.enable
          }
        })
      ];

      if (flags.IsConditionShow()) {
        const { conditions } = values;

        conditions.forEach((condition) => {
          let conditionTarget;
          if (condition.conditionTarget) {
            conditionTarget = `${condition.conditionTarget}`.trim();
          } else {
            conditionTarget = '';
          }
          graphqlActions.push(
            this.props.mutateUpdateCondition({
              variables: {
                appId,
                featureKey: condition.featureKey,
                environment: env,
                conditionType: condition.conditionType,
                conditionTarget,
                conditionRule: condition.conditionRule,
                enable: condition.enable
              }
            })
          );
        });
      }
    }

    Promise.all(graphqlActions)
      .then((response) => {
        const error = _get(response, 'errors[0]');
        if (error) {
          this.showStateFor({ error });
        } else {
          this.showStateFor({ completed: true });
        }
      })
      .catch((error) => {
        this.showStateFor({ error });
      })
      .finally(() => {
        this.resetState();
      });
  };

  getTitle(flag) {
    const { action } = this.props;

    if (action === actions.FEATURE_ADD) {
      return 'Add Feature';
    }
    if (action === actions.FEATURE_DELETE) {
      return 'Delete Feature?';
    }
    if (action === actions.FEATURE_TOGGLE) {
      return flag && flag.enable ? 'Turn off Feature?' : 'Turn on Feature?';
    }
    return 'Edit Feature';
  }

  handleClose = () => {
    this.props.onClose();
  };

  refetchUserSegments = () => {
    const refetch = _get(this.props, 'querylistUserSegments.refetch');
    if (refetch) {
      const refetchTimeout = setTimeout(() => {
        const refetchP = refetch();
        refetchP.then().finally(() => {
          this.setState({ loading: false });
        });
      }, DELAY_AFTER_S3_FILE_UPLOAD);
      this.setState({ loading: true, refetchTimeout });
    }
  };

  showStateFor(state) {
    this.setState({ loading: false, ...state });
  }

  resetState() {
    this.setState(
      { loading: false, error: null, completed: false },
      this.handleClose
    );
  }

  isLoding() {
    const segmentQ = _get(this.props, 'querylistUserSegments.networkStatus');
    const conditionQ = _get(this.props, 'querylistConditions.networkStatus');
    const { loading } = this.state;
    return segmentQ < 7 || conditionQ < 7 || loading;
  }

  isError() {
    const segmentError = _get(this.props, 'querylistUserSegments.error');
    const conditionError = _get(this.props, 'querylistConditions.error');
    const { error } = this.state;
    return segmentError || conditionError || error;
  }

  isFullScreen() {
    const { action } = this.props;
    return action === actions.FEATURE_EDIT;
  }

  render() {
    const { open, action, feature, flag } = this.props;
    const { completed } = this.state;
    const isFullScreen = this.isFullScreen();
    const isLoading = this.isLoding();

    if (!isLoading) {
      const error = this.isError();
      if (error) {
        console.warn(error);
        return null;
      }
    }

    if (completed) {
      const messageContextMap = {
        [actions.FEATURE_ADD]: 'added',
        [actions.FEATURE_EDIT]: 'updated',
        [actions.FEATURE_DELETE]: 'deleted',
        [actions.FEATURE_TOGGLE]: 'updated'
      };

      return (
        <SnackBar
          message={`Feature is ${messageContextMap[action]} successfully!`}
          options={{ variant: 'success' }}
        />
      );
    }

    const userSegments = _get(
      this.props,
      'querylistUserSegments.listUserSegments.items'
    );
    const conditions = flags.IsTargetAppVerisonShow()
      ? _get(
          this.props,
          'querylistConditions.listConditionsWithMultiConditionTypes.items'
        )
      : _get(this.props, 'querylistConditions.listConditions.items');

    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        aria-labelledby="form-dialog-title"
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        fulllScreen={isFullScreen}
      >
        <DialogTitle id="form-dialog-title">{this.getTitle(flag)}</DialogTitle>
        {isLoading && <Loader />}
        <FeatureForm
          onClose={this.handleClose}
          onSubmit={this.onSubmit}
          refetchUserSegments={this.refetchUserSegments}
          userSegments={userSegments}
          conditions={conditions}
          action={action}
          feature={feature}
          flag={flag}
        />
      </Dialog>
    );
  }
}

FeatureModifier.propTypes = {
  open: PropTypes.bool.isRequired,
  appId: PropTypes.string.isRequired,
  env: PropTypes.string.isRequired
};

const prepareGraphqlCompose = (
  flagIsConditionShow,
  flagIsTargetAppVerisonShow
) => {
  return compose(
    graphql(gqlQuerylistUserSegments, {
      name: 'querylistUserSegments',
      skip: (props) =>
        !(props.action === actions.FEATURE_EDIT && flagIsConditionShow),
      options: { notifyOnNetworkStatusChange: true }
    }),
    graphql(
      flagIsTargetAppVerisonShow
        ? gqlQrylistCondsWithMultiConditionType
        : gqlQuerylistConditions,
      {
        name: 'querylistConditions',
        skip: (props) =>
          !(props.action === actions.FEATURE_EDIT && flagIsConditionShow),
        options: (props) => {
          return {
            variables: {
              appId: props.appId,
              environment: props.env,
              featureKey: _get(props, 'feature.key'),
              conditionType: flagIsTargetAppVerisonShow
                ? ['TARGET_USER', 'TARGET_APP_VERSION']
                : 'TARGET_USER'
            }
          };
        }
      }
    ),
    graphql(gqlMutUpdateCondition, {
      name: 'mutateUpdateCondition'
    }),
    graphql(gqlMutUpdateFlag, {
      name: 'mutateUpdateFlag'
    }),
    graphql(gqlMutUpdateFeature, {
      name: 'mutateUpdateFeature'
    }),
    graphql(gqlMutDeleteFeature, {
      name: 'mutateDeleteFeature',
      options: () => {
        return {
          awaitRefetchQueries: true,
          refetchQueries: ['listFeatures', 'listFlags']
        };
      }
    }),
    graphql(gqlMutCreateFeature, {
      name: 'mutateCreateFeature',
      options: () => {
        return {
          awaitRefetchQueries: true,
          refetchQueries: ['listFeatures', 'listFlags']
        };
      }
    })
  )(FeatureModifier);
};

export default (props) => {
  const flagIsConditionShow = flags.IsConditionShow();
  const flagIsTargetAppVerisonShow = flags.IsTargetAppVerisonShow();

  const GraphqlComposedComp = React.useMemo(() => {
    return prepareGraphqlCompose(
      flagIsConditionShow,
      flagIsTargetAppVerisonShow
    );
  }, [flagIsConditionShow, flagIsTargetAppVerisonShow]);

  return <GraphqlComposedComp {...props} />;
};
